// trackingPayload.formData/formLabels are only for standard CRM field keys.
// Non-standard/custom fields must go through customFields/fileFields/imageDataFields
// using the id returned by register_custom_field. Labels stay human-readable.
type StandardTrackingFieldKey = string;
type RegisteredCustomFieldId = string;
type TrackingCustomField = { value?: unknown; label: string };
type TrackingFileField = { file?: File; files?: File[]; label: string };
type TrackingImageDataField = { dataUrl?: string; label: string };

const TRACKING_ID = "tk_a6161f20e6d148cbbbd44f6b6252ea11";
const LOCATION_ID = "xfaKqeYCXXqhpM9mQvnB";
const PROJECT_ID = "1786557593842074307";

export const postTrackingEvent = (
  trackingPayload: Record<string, unknown> & {
    formData: Record<StandardTrackingFieldKey, unknown>;
    formLabels: Record<StandardTrackingFieldKey, string>;
  },
  options: {
    customFields?: Record<RegisteredCustomFieldId, TrackingCustomField>;
    fileFields?: Record<RegisteredCustomFieldId, TrackingFileField>;
    imageDataFields?: Record<RegisteredCustomFieldId, TrackingImageDataField>;
  } = {},
): Promise<boolean> => {
  const { customFields = {}, fileFields = {}, imageDataFields = {} } = options;
  const eventPayload = {
    ...trackingPayload,
    formData: { ...trackingPayload.formData },
    formLabels: { ...trackingPayload.formLabels },
  };
  const body = new FormData();

  for (const [key, field] of Object.entries(customFields)) {
    if (field.value === undefined) continue;
    eventPayload.formData[key] = field.value;
    eventPayload.formLabels[key] = field.label;
  }

  for (const [key, field] of Object.entries(imageDataFields)) {
    const dataUrl = field.dataUrl;
    if (!dataUrl) continue;
    if (!dataUrl.startsWith("data:image/")) {
      throw new Error("Image data field must be a data:image/* base64 string");
    }
    eventPayload.formData[key] = dataUrl;
    eventPayload.formLabels[key] = field.label;
  }

  for (const [key, field] of Object.entries(fileFields)) {
    const files = field.files ?? (field.file ? [field.file] : []);
    if (files.length === 0) continue;
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        throw new Error("File must be 50 MB or smaller");
      }
      body.append(key, file, file.name);
    }
    eventPayload.formData[key] = files.map((f) => ({
      filename: f.name,
      size: f.size,
      type: f.type || "application/octet-stream",
    }));
    eventPayload.formLabels[key] = field.label;
  }

  for (const key of Object.keys(eventPayload.formData)) {
    eventPayload.formLabels[key] ||= key;
  }

  body.append("event", JSON.stringify(eventPayload));

  return fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
    method: "POST",
    headers: {
      version: "2021-07-28",
    },
    body,
  }).then((res) => res.ok);
};

export const buildTrackingPayload = (formId: string, formName: string) => ({
  type: "external form_submission" as const,
  timestamp: Date.now(),
  formId,
  formData: {} as Record<string, unknown>,
  formLabels: {} as Record<string, string>,
  url: window.location.href,
  title: document.title,
  path: window.location.pathname,
  userAgent: navigator.userAgent,
  trackingId: TRACKING_ID,
  locationId: LOCATION_ID,
  projectId: PROJECT_ID,
  sessionId: crypto.randomUUID(),
  properties: {
    deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
      ? "mobile"
      : "desktop",
    source: "ai_studio",
    projectId: PROJECT_ID,
    formName,
  },
});
